import { formatMediaPlaybackTime } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { HatTextTooltip } from "@/components/tooltip";
import { formatNumber } from "@/utils";
import { formatEpisodeDuration } from "../../datasetFormatters";
import type {
  ActionSourceBand,
  ActionSourceModel,
  ActionSourceTotal,
} from "./actionSourceSegments";
import { actionSourceDotColor, actionSourceTerm } from "./actionSourceTerms";
import { bandLengthText } from "./segmentTimeline";
import styles from "../diagnosticsTables.module.css";

/**
 * 구간 보기. 출처별 합계와 각 띠를 표시한다.
 *
 * 값은 모두 구간 자체의 사실(출처·시각·길이·행 수)이다. 위상 grid 는 행 인덱스 bucket 이다.
 * 그래서 시간 구간으로 잘라 수치를 내면 틀린다. 따라서 구간별 운동 지표는 여기서 만들지 않는다.
 * ZERG-603 이 지표를 저장하면 띠 id 로 연결한다.
 */
export function SegmentsView({
  model,
  onSeekToSeconds,
}: {
  model: ActionSourceModel;
  onSeekToSeconds?: (seconds: number) => void;
}): ReactNode {
  return (
    <div className={styles.view}>
      <section className={styles.section} aria-label="출처별 합계">
        <span className={styles.title}>출처별 합계</span>
        <div className={styles.scroll}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">출처</th>
                <th scope="col">합계</th>
                <th scope="col">비율</th>
                <th scope="col">구간</th>
                <th scope="col">행</th>
              </tr>
            </thead>
            <tbody>
              {model.totals.map((total) => (
                <TotalRow key={total.category} total={total} />
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className={styles.section} aria-label="구간">
        <span className={styles.title}>구간</span>
        <div className={styles.scroll}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">출처</th>
                <th scope="col">시작</th>
                <th scope="col">끝</th>
                <th scope="col">길이</th>
                <th scope="col">행</th>
              </tr>
            </thead>
            <tbody>
              {model.bands.map((band, index) => (
                <BandRow
                  key={band.id}
                  index={index}
                  band={band}
                  onSeekToSeconds={onSeekToSeconds}
                />
              ))}
            </tbody>
          </table>
        </div>
        <span className={styles.note}>{factsText(model)}</span>
      </section>
    </div>
  );
}

function SourceLabel({ category, code }: Pick<ActionSourceBand, "category" | "code">): ReactNode {
  const term = actionSourceTerm(category, code);
  return (
    <HatTextTooltip text={term.tip} placement="bottom" align="start">
      <span>
        <span
          className={styles.dot}
          style={{ background: actionSourceDotColor(term.dataViz) }}
          aria-hidden="true"
        />
        {term.label}
      </span>
    </HatTextTooltip>
  );
}

function TotalRow({ total }: { total: ActionSourceTotal }): ReactNode {
  return (
    <tr>
      <th scope="row">
        <SourceLabel category={total.category} code={null} />
      </th>
      <td>{formatEpisodeDuration(total.seconds) ?? "—"}</td>
      <td>{Math.round(total.share * 100)}%</td>
      <td>{formatNumber(total.bandCount)}</td>
      <td>{formatNumber(total.nRows)}</td>
    </tr>
  );
}

function BandRow({
  index,
  band,
  onSeekToSeconds,
}: {
  index: number;
  band: ActionSourceBand;
  onSeekToSeconds?: (seconds: number) => void;
}): ReactNode {
  const start = formatMediaPlaybackTime(Math.max(0, band.startSeconds));
  return (
    <tr>
      <th scope="row">{index + 1}</th>
      <td className={styles.labelCell}>
        <SourceLabel category={band.category} code={band.code} />
      </td>
      <td>
        {onSeekToSeconds === undefined ? (
          start
        ) : (
          <button
            type="button"
            className={styles.seekButton}
            aria-label={`${start} — 이 구간 처음으로 영상을 옮깁니다.`}
            onClick={() => onSeekToSeconds(Math.max(0, band.startSeconds))}
          >
            {start}
          </button>
        )}
      </td>
      <td>{formatMediaPlaybackTime(band.endSeconds)}</td>
      <td>{bandLengthText(band)}</td>
      <td>{formatNumber(band.nRows)}</td>
    </tr>
  );
}

// 띠로 합치면서 가려진 원본의 사실. gap 은 앞 구간에 합쳤다. 무효 값과 정의 밖 값은 띠로 남는다.
function factsText(model: ActionSourceModel): string {
  const { facts } = model;
  const parts = [
    facts.gapCount === 0
      ? "끊김 없음"
      : `끊김 ${formatNumber(facts.gapCount)}회(최대 ${(facts.maxGapSeconds ?? 0).toFixed(2)}초, 앞 구간에 합쳤습니다)`,
  ];
  if (facts.invalidRows > 0) parts.push(`무효 값 ${formatNumber(facts.invalidRows)}행`);
  if (facts.unknownRows > 0) parts.push(`정의 밖 값 ${formatNumber(facts.unknownRows)}행`);
  if (facts.skippedElements > 0) {
    parts.push(`시각이 없어 뺀 원소 ${formatNumber(facts.skippedElements)}개`);
  }
  return parts.join(" · ");
}
